import dagre from 'dagre';

const NODE_WIDTH = 150;
const NODE_HEIGHT = 50;
const HORIZONTAL_SPACING = 80;
const VERTICAL_SPACING = 40;
const SIBLING_SPACING = 20; // Vertical spacing between sibling nodes

/**
 * Build a graph structure from mindmap elements
 * @param {Array} elements - All canvas elements
 * @returns {Object} - { nodes: Map, edges: Array, roots: Array }
 */
export const buildMindmapGraph = (elements) => {
  const mindmapNodes = new Map();
  const edges = [];
  const childIds = new Set();

  // Collect all mindmap nodes
  elements.forEach(el => {
    if (el.type === 'mindmap') {
      mindmapNodes.set(el.id, el);
    }
  });

  // Collect edges from connectors
  elements.forEach(el => {
    if (el.type === 'connector' && el.startAnchor?.shapeId && el.endAnchor?.shapeId) {
      const startNode = mindmapNodes.get(el.startAnchor.shapeId);
      const endNode = mindmapNodes.get(el.endAnchor.shapeId);
      
      if (startNode && endNode) {
        edges.push({
          source: el.startAnchor.shapeId,
          target: el.endAnchor.shapeId,
          connectorId: el.id
        });
        childIds.add(el.endAnchor.shapeId);
      }
    }
  });

  // Find root nodes (nodes that are not children of any other node)
  const roots = [];
  mindmapNodes.forEach((node, id) => {
    if (!childIds.has(id)) {
      roots.push(id);
    }
  });

  return { nodes: mindmapNodes, edges, roots };
};

/**
 * Get all nodes in a connected mindmap tree starting from a root
 * @param {string} rootId - The root node ID
 * @param {Map} nodes - All mindmap nodes
 * @param {Array} edges - All edges
 * @returns {Set} - Set of node IDs in this tree
 */
const getTreeNodes = (rootId, nodes, edges) => {
  const treeNodes = new Set([rootId]);
  const queue = [rootId];
  
  while (queue.length > 0) {
    const currentId = queue.shift();
    edges.forEach(edge => {
      if (edge.source === currentId && !treeNodes.has(edge.target)) {
        treeNodes.add(edge.target);
        queue.push(edge.target);
      }
    });
  }
  
  return treeNodes;
};

/**
 * Apply dagre layout to a mindmap tree
 * @param {string} rootId - The root node ID
 * @param {Map} nodes - All mindmap nodes
 * @param {Array} edges - All edges between mindmap nodes
 * @param {Object} rootPosition - Optional position to anchor the root { x, y }
 * @returns {Object} - { nodePositions: Map<id, {x, y}>, connectorUpdates: Array }
 */
export const layoutMindmapTree = (rootId, nodes, edges, rootPosition = null) => {
  const g = new dagre.graphlib.Graph();
  
  // Set graph options for horizontal tree layout (LR = left to right)
  g.setGraph({
    rankdir: 'LR',
    nodesep: VERTICAL_SPACING,
    ranksep: HORIZONTAL_SPACING,
    marginx: 20,
    marginy: 20
  });
  
  g.setDefaultEdgeLabel(() => ({}));

  // Get all nodes in this tree
  const treeNodeIds = getTreeNodes(rootId, nodes, edges);
  
  // Add nodes to the graph
  treeNodeIds.forEach(nodeId => {
    const node = nodes.get(nodeId);
    if (node) {
      g.setNode(nodeId, {
        width: node.width || NODE_WIDTH,
        height: node.height || NODE_HEIGHT
      });
    }
  });

  // Add edges to the graph (only edges within this tree)
  edges.forEach(edge => {
    if (treeNodeIds.has(edge.source) && treeNodeIds.has(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  // Run the layout
  dagre.layout(g);

  // Calculate offset to maintain root position if specified
  const rootNode = g.node(rootId);
  let offsetX = 0;
  let offsetY = 0;
  
  if (rootPosition && rootNode) {
    const currentRootNode = nodes.get(rootId);
    const rootWidth = currentRootNode?.width || NODE_WIDTH;
    const rootHeight = currentRootNode?.height || NODE_HEIGHT;
    
    // Dagre gives center position, we need top-left
    offsetX = rootPosition.x - (rootNode.x - rootWidth / 2);
    offsetY = rootPosition.y - (rootNode.y - rootHeight / 2);
  }

  // Extract positions (convert from center to top-left coordinates)
  const nodePositions = new Map();
  treeNodeIds.forEach(nodeId => {
    const layoutNode = g.node(nodeId);
    const originalNode = nodes.get(nodeId);
    if (layoutNode && originalNode) {
      const width = originalNode.width || NODE_WIDTH;
      const height = originalNode.height || NODE_HEIGHT;
      nodePositions.set(nodeId, {
        x: layoutNode.x - width / 2 + offsetX,
        y: layoutNode.y - height / 2 + offsetY
      });
    }
  });

  return { nodePositions, treeNodeIds };
};

/**
 * Calculate connector anchor points based on relative positions
 * @param {Object} sourceNode - Source node with x, y, width, height
 * @param {Object} targetNode - Target node with x, y, width, height
 * @returns {Object} - { startAnchor, endAnchor, points }
 */
export const calculateConnectorAnchors = (sourceNode, targetNode) => {
  const sourceCenterX = sourceNode.x + (sourceNode.width || NODE_WIDTH) / 2;
  const sourceCenterY = sourceNode.y + (sourceNode.height || NODE_HEIGHT) / 2;
  const targetCenterX = targetNode.x + (targetNode.width || NODE_WIDTH) / 2;
  const targetCenterY = targetNode.y + (targetNode.height || NODE_HEIGHT) / 2;

  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;
  const angle = Math.atan2(dy, dx);

  let sourceSide, targetSide;
  let sourceX, sourceY, targetX, targetY;

  // Determine sides based on angle
  if (angle >= -Math.PI / 4 && angle < Math.PI / 4) {
    // Target is to the right
    sourceSide = 'right';
    targetSide = 'left';
    sourceX = sourceNode.x + (sourceNode.width || NODE_WIDTH);
    sourceY = sourceCenterY;
    targetX = targetNode.x;
    targetY = targetCenterY;
  } else if (angle >= Math.PI / 4 && angle < 3 * Math.PI / 4) {
    // Target is below
    sourceSide = 'bottom';
    targetSide = 'top';
    sourceX = sourceCenterX;
    sourceY = sourceNode.y + (sourceNode.height || NODE_HEIGHT);
    targetX = targetCenterX;
    targetY = targetNode.y;
  } else if (angle >= -3 * Math.PI / 4 && angle < -Math.PI / 4) {
    // Target is above
    sourceSide = 'top';
    targetSide = 'bottom';
    sourceX = sourceCenterX;
    sourceY = sourceNode.y;
    targetX = targetCenterX;
    targetY = targetNode.y + (targetNode.height || NODE_HEIGHT);
  } else {
    // Target is to the left
    sourceSide = 'left';
    targetSide = 'right';
    sourceX = sourceNode.x;
    sourceY = sourceCenterY;
    targetX = targetNode.x + (targetNode.width || NODE_WIDTH);
    targetY = targetCenterY;
  }

  return {
    startAnchor: { shapeId: sourceNode.id, side: sourceSide },
    endAnchor: { shapeId: targetNode.id, side: targetSide },
    points: [sourceX, sourceY, targetX, targetY]
  };
};

/**
 * Apply layout to all mindmap trees and update elements
 * @param {Array} elements - All canvas elements
 * @param {string} anchorRootId - Optional: keep this root's position fixed
 * @returns {Array} - Updated elements with new positions
 */
export const applyMindmapLayout = (elements, anchorRootId = null) => {
  const { nodes, edges, roots } = buildMindmapGraph(elements);
  
  if (roots.length === 0 || nodes.size === 0) {
    return elements;
  }

  // Layout each tree separately
  const allPositions = new Map();
  
  roots.forEach(rootId => {
    const rootNode = nodes.get(rootId);
    const rootPosition = rootId === anchorRootId || anchorRootId === null
      ? { x: rootNode.x, y: rootNode.y }
      : null;
    
    const { nodePositions } = layoutMindmapTree(rootId, nodes, edges, rootPosition || { x: rootNode.x, y: rootNode.y });
    nodePositions.forEach((pos, id) => allPositions.set(id, pos));
  });

  // Update elements with new positions
  const updatedElements = elements.map(el => {
    if (el.type === 'mindmap' && allPositions.has(el.id)) {
      const newPos = allPositions.get(el.id);
      return { ...el, x: newPos.x, y: newPos.y };
    }
    return el;
  });

  // Update connectors based on new node positions
  const updatedNodesMap = new Map();
  updatedElements.forEach(el => {
    if (el.type === 'mindmap') {
      updatedNodesMap.set(el.id, el);
    }
  });

  const finalElements = updatedElements.map(el => {
    if (el.type === 'connector' && el.startAnchor?.shapeId && el.endAnchor?.shapeId) {
      const sourceNode = updatedNodesMap.get(el.startAnchor.shapeId);
      const targetNode = updatedNodesMap.get(el.endAnchor.shapeId);
      
      if (sourceNode && targetNode) {
        const anchors = calculateConnectorAnchors(sourceNode, targetNode);
        return {
          ...el,
          points: anchors.points,
          startAnchor: anchors.startAnchor,
          endAnchor: anchors.endAnchor
        };
      }
    }
    return el;
  });

  return finalElements;
};

/**
 * Find the root of a mindmap tree given any node in it
 * @param {string} nodeId - Any node ID in the tree
 * @param {Array} elements - All canvas elements
 * @returns {string|null} - Root node ID or null
 */
export const findMindmapRoot = (nodeId, elements) => {
  const { nodes, edges } = buildMindmapGraph(elements);
  
  if (!nodes.has(nodeId)) {
    return null;
  }

  // Build parent map
  const parentMap = new Map();
  edges.forEach(edge => {
    parentMap.set(edge.target, edge.source);
  });

  // Traverse up to find root
  let currentId = nodeId;
  while (parentMap.has(currentId)) {
    currentId = parentMap.get(currentId);
  }

  return currentId;
};

/**
 * Find an optimal position for a new child node without moving existing nodes.
 * Places the new node to avoid overlapping with existing siblings.
 * @param {Object} parentNode - The parent node
 * @param {Array} existingSiblings - Array of existing sibling nodes (children of same parent)
 * @param {Object} newNodeDimensions - { width, height } of the new node
 * @returns {Object} - { x, y } position for the new node
 */
export const findOptimalChildPosition = (parentNode, existingSiblings, newNodeDimensions = { width: NODE_WIDTH, height: NODE_HEIGHT }) => {
  const parentCenterX = parentNode.x + (parentNode.width || NODE_WIDTH) / 2;
  const parentCenterY = parentNode.y + (parentNode.height || NODE_HEIGHT) / 2;
  const parentRight = parentNode.x + (parentNode.width || NODE_WIDTH);
  
  // Default position: to the right of parent
  const baseX = parentRight + HORIZONTAL_SPACING;
  
  if (existingSiblings.length === 0) {
    // First child: place centered vertically with parent
    return {
      x: baseX,
      y: parentCenterY - newNodeDimensions.height / 2
    };
  }
  
  // Collect all occupied vertical ranges from siblings (regardless of their X position)
  // We only care about siblings that could potentially overlap horizontally with the new node
  const relevantSiblings = existingSiblings.filter(sibling => {
    // Consider siblings that are roughly in the same horizontal zone
    const siblingLeft = sibling.x;
    const siblingRight = sibling.x + (sibling.width || NODE_WIDTH);
    const newNodeLeft = baseX;
    const newNodeRight = baseX + newNodeDimensions.width;
    
    // Check for horizontal overlap or proximity
    return !(siblingRight < newNodeLeft - 50 || siblingLeft > newNodeRight + 50);
  });
  
  if (relevantSiblings.length === 0) {
    // No relevant siblings, place centered with parent
    return {
      x: baseX,
      y: parentCenterY - newNodeDimensions.height / 2
    };
  }
  
  // Sort siblings by Y position
  const sortedSiblings = [...relevantSiblings].sort((a, b) => a.y - b.y);
  
  // Find gaps between siblings
  const gaps = [];
  
  // Gap above first sibling
  const firstSibling = sortedSiblings[0];
  gaps.push({
    y: firstSibling.y - SIBLING_SPACING - newNodeDimensions.height,
    distanceFromParent: Math.abs((firstSibling.y - SIBLING_SPACING - newNodeDimensions.height + newNodeDimensions.height / 2) - parentCenterY)
  });
  
  // Gaps between siblings
  for (let i = 0; i < sortedSiblings.length - 1; i++) {
    const current = sortedSiblings[i];
    const next = sortedSiblings[i + 1];
    const currentBottom = current.y + (current.height || NODE_HEIGHT);
    const gapSize = next.y - currentBottom;
    
    if (gapSize >= newNodeDimensions.height + SIBLING_SPACING) {
      const gapY = currentBottom + SIBLING_SPACING;
      gaps.push({
        y: gapY,
        distanceFromParent: Math.abs((gapY + newNodeDimensions.height / 2) - parentCenterY)
      });
    }
  }
  
  // Gap below last sibling
  const lastSibling = sortedSiblings[sortedSiblings.length - 1];
  const belowY = lastSibling.y + (lastSibling.height || NODE_HEIGHT) + SIBLING_SPACING;
  gaps.push({
    y: belowY,
    distanceFromParent: Math.abs((belowY + newNodeDimensions.height / 2) - parentCenterY)
  });
  
  // Choose the gap closest to parent's vertical center
  gaps.sort((a, b) => a.distanceFromParent - b.distanceFromParent);
  
  return {
    x: baseX,
    y: gaps[0].y
  };
};

/**
 * Get existing children of a parent node
 * @param {string} parentId - Parent node ID
 * @param {Array} elements - All canvas elements
 * @returns {Array} - Array of child node objects
 */
export const getChildNodes = (parentId, elements) => {
  const childIds = new Set();
  
  // Find all connectors starting from this parent
  elements.forEach(el => {
    if (el.type === 'connector' && el.startAnchor?.shapeId === parentId && el.endAnchor?.shapeId) {
      childIds.add(el.endAnchor.shapeId);
    }
  });
  
  // Get the actual child nodes
  return elements.filter(el => childIds.has(el.id) && el.type === 'mindmap');
};
